import type {
  Lks94Horizontal,
  LocalHorizontal,
  LocalOrigin,
} from "./coordinates.ts";
import { localHorizontalToLks94 } from "./coordinates.ts";
import type { DevelopmentWorldTile, TerrainTileData } from "./schema/tile.ts";

export interface SourceTerrainGrid {
  origin: Lks94Horizontal;
  spacingM: number;
  width: number;
  height: number;
  /** Row-major source elevation. Row zero is the southernmost row. */
  heightsM: readonly number[];
}

export interface HeightQuery {
  sampleHeight(position: Readonly<LocalHorizontal>): number | null;
}

export class SourceTerrainSampler implements HeightQuery {
  private readonly grid: Readonly<SourceTerrainGrid>;
  private readonly localOrigin: Readonly<LocalOrigin>;

  public constructor(
    grid: Readonly<SourceTerrainGrid>,
    localOrigin: Readonly<LocalOrigin>,
  ) {
    validateSourceTerrainGrid(grid);
    this.grid = grid;
    this.localOrigin = localOrigin;
  }

  public sampleHeight(position: Readonly<LocalHorizontal>): number | null {
    const projected = localHorizontalToLks94(position, this.localOrigin);
    const gridX = (projected.easting - this.grid.origin.easting) /
      this.grid.spacingM;
    const gridY = (projected.northing - this.grid.origin.northing) /
      this.grid.spacingM;

    const epsilon = 1e-9;
    if (
      gridX < -epsilon ||
      gridY < -epsilon ||
      gridX > this.grid.width - 1 + epsilon ||
      gridY > this.grid.height - 1 + epsilon
    ) {
      return null;
    }

    const boundedX = Math.min(this.grid.width - 1, Math.max(0, gridX));
    const boundedY = Math.min(this.grid.height - 1, Math.max(0, gridY));
    const x0 = Math.floor(boundedX);
    const y0 = Math.floor(boundedY);
    const x1 = Math.min(x0 + 1, this.grid.width - 1);
    const y1 = Math.min(y0 + 1, this.grid.height - 1);
    const alphaX = boundedX - x0;
    const alphaY = boundedY - y0;

    const southWest = this.read(x0, y0);
    const southEast = this.read(x1, y0);
    const northWest = this.read(x0, y1);
    const northEast = this.read(x1, y1);
    const south = southWest + (southEast - southWest) * alphaX;
    const north = northWest + (northEast - northWest) * alphaX;
    const elevationM = south + (north - south) * alphaY;
    return elevationM - this.localOrigin.elevationM;
  }

  private read(x: number, y: number): number {
    const value = this.grid.heightsM[y * this.grid.width + x];
    if (value === undefined) {
      throw new Error(`Terrain sample (${x}, ${y}) is outside its grid`);
    }
    return value;
  }
}

export function sampleTileTerrain(
  tile: Readonly<DevelopmentWorldTile>,
  position: Readonly<LocalHorizontal>,
): number | null {
  return sampleTerrainTileData(tile.terrain, tile.origin, position);
}

export function sampleTerrainTileData(
  terrain: Readonly<TerrainTileData>,
  tileOrigin: Readonly<LocalHorizontal>,
  position: Readonly<LocalHorizontal>,
): number | null {
  const localX = position.x - tileOrigin.x;
  const localZ = position.z - tileOrigin.z;
  const maximum = terrain.spacingM * (terrain.samplesPerSide - 1);
  const epsilon = 1e-9;
  if (
    localX < -epsilon ||
    localZ < -epsilon ||
    localX > maximum + epsilon ||
    localZ > maximum + epsilon
  ) {
    return null;
  }

  const gridX = Math.min(maximum, Math.max(0, localX)) / terrain.spacingM;
  const gridZ = Math.min(maximum, Math.max(0, localZ)) / terrain.spacingM;
  const x0 = Math.floor(gridX);
  const z0 = Math.floor(gridZ);
  const x1 = Math.min(x0 + 1, terrain.samplesPerSide - 1);
  const z1 = Math.min(z0 + 1, terrain.samplesPerSide - 1);
  const alphaX = gridX - x0;
  const alphaZ = gridZ - z0;
  const northWest = readTerrainSample(terrain, x0, z0);
  const northEast = readTerrainSample(terrain, x1, z0);
  const southWest = readTerrainSample(terrain, x0, z1);
  const southEast = readTerrainSample(terrain, x1, z1);
  // Runtime terrain uses the north-east to south-west diagonal in every grid
  // cell. Sample those same two planes so grounded objects and draped overlays
  // cannot diverge from the rendered surface through bilinear interpolation.
  if (alphaX + alphaZ <= 1) {
    return northWest +
      (northEast - northWest) * alphaX +
      (southWest - northWest) * alphaZ;
  }
  return southEast +
    (northEast - southEast) * (1 - alphaZ) +
    (southWest - southEast) * (1 - alphaX);
}

function readTerrainSample(
  terrain: Readonly<TerrainTileData>,
  x: number,
  z: number,
): number {
  const value = terrain.heightsM[z * terrain.samplesPerSide + x];
  if (value === undefined) {
    throw new Error(`Terrain tile sample (${x}, ${z}) is missing`);
  }
  return value;
}

function validateSourceTerrainGrid(grid: Readonly<SourceTerrainGrid>): void {
  if (
    !Number.isSafeInteger(grid.width) ||
    !Number.isSafeInteger(grid.height) ||
    grid.width < 2 ||
    grid.height < 2
  ) {
    throw new Error("Terrain grid dimensions must both be integers >= 2");
  }
  if (!Number.isFinite(grid.spacingM) || grid.spacingM <= 0) {
    throw new Error("Terrain spacing must be a positive finite number");
  }
  if (grid.heightsM.length !== grid.width * grid.height) {
    throw new Error(
      `Terrain grid expected ${grid.width * grid.height} samples, received ${grid.heightsM.length}`,
    );
  }
  if (grid.heightsM.some((height) => !Number.isFinite(height))) {
    throw new Error("Terrain elevations must all be finite");
  }
}
